import React, { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { useHistory, useParams } from "react-router-dom";
import { Modal } from "@mui/material";
import { SPANISH_ABBREV, getAvailableLanguages } from "@bto/shared";
import { useTranslation } from "react-i18next";
import {
  DetailedReportTop,
  CarrierGeneralContainer,
  CarrierDesktopLeftWrapper,
  CarrierDesktopRightWrapper,
  CarrierDesktopWrapper,
  CarrierTitleWrapper,
  CarrierWrapper,
  CarrierTabletMobileWrapper,
  CarrierBackToResults,
} from "./Carrier.styles";
import { GetTypographyContent } from "../../components/shared/Typography/Typography";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { DetailedResultsHeader } from "../../components/DetailedResults/DetailedResultsHeader";
import { DetailedResultsTable } from "../../components/DetailedResults/DetailedResultsTable";
import { DetailedResultsViewReport } from "../../components/DetailedResults/DetailedResultsViewReport";
import { DetailedResultsExpert } from "../../components/DetailedResults/DetailedResultsExpert";
import { DetailedResultsFAQ } from "../../components/DetailedResults/DetailedResultsFAQ";
import {
  getAllRequisitions,
  getSpecificFetalSexCs,
  getSpecificUnityLowRisk,
  getSpecificUnityReportID,
  getSpecificUnityStatus,
  getUserAuth,
  getSpecificUnityPdfUrls,
  getSpecificCarrierReportVideos,
} from "../../recoil/selectors";
import { LANDING_PAGE_URL } from "../../utils/urlConstants";
import { BackIcon } from "../../assets/iconComponents/BackIcon";
import {
  useReportDownload,
  shouldShowCarrierFetalSexWarning,
} from "../../utils/hooks/useReportDownload";
import { FetalSexDownloadWarningModal } from "../../components/DetailedResults/FetalSexDownloadWarningModal";
import { FetalSexModalState } from "../../components/DetailedResults/DetailedResults.types";
import { SendActionType } from "../../components/shared/Services/SharedServices";
import ReportVideoEmbed from "../../components/shared/Video/ReportVideoEmbed";
import TestResultsMultipleReqHeader from "../../components/TestResults/TestResultsMultipleReqHeader";
import { useBooleanFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";

const Landing = (): React.ReactElement => {
  const { deviceType } = useDeviceHook();
  const { t } = useTranslation();
  const { requisitionID } = useParams<{ requisitionID: string }>();
  const requisitions = useRecoilValue(getAllRequisitions);
  const isLowRisk = useRecoilValue(getSpecificUnityLowRisk(requisitionID));
  const status = useRecoilValue(getSpecificUnityStatus(requisitionID));
  const history = useHistory();
  const reportID = useRecoilValue(getSpecificUnityReportID(requisitionID));
  const auth = useRecoilValue(getUserAuth);
  const fetalSex = useRecoilValue(getSpecificFetalSexCs(requisitionID));
  const pdfUrlsByLanguage = useRecoilValue(
    getSpecificUnityPdfUrls(requisitionID),
  );
  const reportVideos = useRecoilValue(
    getSpecificCarrierReportVideos(requisitionID),
  );
  const { enabled: isCarrierResultVideosEnabled } = useBooleanFeatureFlag(
    FeatureFlags.CarrierResultVideos,
  );
  const availableLanguages = getAvailableLanguages(pdfUrlsByLanguage);
  const [showFetalSexModal, setShowFetalSexModal] =
    useState<FetalSexModalState>("");

  useEffect(() => {
    const sendUIAction = async () => {
      try {
        await SendActionType(
          "ViewCarrierPage",
          requisitionID,
          auth?.access_token,
          {
            result_available: String(
              status !== "Not Ordered" &&
                status !== "Processing" &&
                status !== "Ordered",
            ),
            report_summary: isLowRisk ? "Low Risk" : "See Report",
          },
        );
      } catch (e) {
        return undefined;
      }
    };
    sendUIAction();
  }, []);

  const { downloadReport } = useReportDownload();

  const ModalComponent = (
    <Modal
      open={Boolean(showFetalSexModal)}
      onClose={() => setShowFetalSexModal("")}
      style={{
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
      }}
    >
      <FetalSexDownloadWarningModal
        analyticsPageName="CarrierReportPage"
        onConfirmDownload={() =>
          downloadReport(
            reportID,
            "carrier",
            showFetalSexModal === "report_es" ? SPANISH_ABBREV : undefined,
            availableLanguages,
          )
        }
        showModal={setShowFetalSexModal}
      />
    </Modal>
  );

  useEffect(() => {
    if (auth && (!status || !(isLowRisk !== undefined))) {
      history.push(LANDING_PAGE_URL);
    }
  }, [status, isLowRisk, auth]);

  const statusValid =
    status && status !== "Processing" && status !== "Not Ordered";

  if (deviceType === "desktop") {
    return (
      <>
        {ModalComponent}
        <CarrierWrapper>
          <CarrierDesktopWrapper>
            <CarrierDesktopLeftWrapper
              multipleReqs={Boolean(requisitions && requisitions.length > 1)}
            >
              <CarrierBackToResults
                onClick={() => history.push(LANDING_PAGE_URL)}
              >
                <BackIcon />
                {GetTypographyContent({
                  content: t("results.actions.backToTests"),
                  size: "medium",
                  textColor: "redMedium",
                })}
              </CarrierBackToResults>
              <CarrierTitleWrapper>
                {GetTypographyContent({
                  content: t("results.pages.carrierTitle"),
                  size: "h2",
                  textColor: "greyMedium",
                  isBold: true,
                  padding: "0 0 0 10px",
                })}
              </CarrierTitleWrapper>
              <TestResultsMultipleReqHeader />
              <CarrierGeneralContainer style={{ height: "fit-content" }}>
                <DetailedReportTop>
                  <DetailedResultsHeader
                    type="carrier"
                    requisitionID={requisitionID}
                  />
                  {statusValid && isLowRisk && (
                    <DetailedResultsTable
                      requisitionID={requisitionID}
                      isAneuploidy={false}
                    />
                  )}
                  {statusValid && (
                    <DetailedResultsViewReport
                      fetalSexWarning={shouldShowCarrierFetalSexWarning(
                        fetalSex,
                      )}
                      setShowFetalSexModal={setShowFetalSexModal}
                      reportDownload={() =>
                        downloadReport(
                          reportID,
                          "carrier",
                          undefined,
                          availableLanguages,
                        )
                      }
                      spanishReportDownload={
                        pdfUrlsByLanguage?.[SPANISH_ABBREV]
                          ? () =>
                              downloadReport(
                                reportID,
                                "carrier",
                                SPANISH_ABBREV,
                                availableLanguages,
                              )
                          : undefined
                      }
                    />
                  )}
                </DetailedReportTop>
              </CarrierGeneralContainer>
              {statusValid &&
                isCarrierResultVideosEnabled &&
                reportVideos.map((video, index) => (
                  <CarrierGeneralContainer
                    key={`${video.analytics_prefix}-${video.video_url}`}
                    style={{ padding: "30px 15px 30px 16px" }}
                    id={index === 0 ? "carrierInfoVideo" : undefined}
                  >
                    <ReportVideoEmbed
                      reportType="carrier"
                      url={video.video_url}
                      analyticsPrefix={video.analytics_prefix}
                      requisitionID={requisitionID}
                    />
                  </CarrierGeneralContainer>
                ))}
            </CarrierDesktopLeftWrapper>
            <CarrierDesktopRightWrapper>
              <div />
              <div />
              <CarrierGeneralContainer>
                <DetailedResultsExpert />
              </CarrierGeneralContainer>
              <CarrierGeneralContainer>
                <DetailedResultsFAQ />
              </CarrierGeneralContainer>
            </CarrierDesktopRightWrapper>
          </CarrierDesktopWrapper>
        </CarrierWrapper>
      </>
    );
  }
  return (
    <>
      {ModalComponent}
      <CarrierWrapper>
        <CarrierTabletMobileWrapper deviceType={deviceType}>
          <CarrierBackToResults onClick={() => history.push(LANDING_PAGE_URL)}>
            <BackIcon />
            {GetTypographyContent({
              content: t("results.actions.backToTests"),
              size: "medium",
              textColor: "redMedium",
            })}
          </CarrierBackToResults>
          <CarrierTitleWrapper>
            {GetTypographyContent({
              content: t("results.pages.carrierTitle"),
              size: "h2",
              textColor: "greyMedium",
              isBold: true,
              padding: "0 0 0 10px",
            })}
          </CarrierTitleWrapper>
          <TestResultsMultipleReqHeader />
          <CarrierGeneralContainer style={{ height: "fit-content" }}>
            <DetailedReportTop>
              <DetailedResultsHeader
                type="carrier"
                requisitionID={requisitionID}
              />
              {statusValid && isLowRisk && (
                <DetailedResultsTable
                  requisitionID={requisitionID}
                  isAneuploidy={false}
                />
              )}
              {statusValid && (
                <DetailedResultsViewReport
                  fetalSexWarning={shouldShowCarrierFetalSexWarning(fetalSex)}
                  setShowFetalSexModal={setShowFetalSexModal}
                  reportDownload={() =>
                    downloadReport(
                      reportID,
                      "carrier",
                      undefined,
                      availableLanguages,
                    )
                  }
                  spanishReportDownload={
                    pdfUrlsByLanguage?.[SPANISH_ABBREV]
                      ? () =>
                          downloadReport(
                            reportID,
                            "carrier",
                            SPANISH_ABBREV,
                            availableLanguages,
                          )
                      : undefined
                  }
                />
              )}
            </DetailedReportTop>
          </CarrierGeneralContainer>
          {statusValid &&
            isCarrierResultVideosEnabled &&
            reportVideos.map((video, index) => (
              <CarrierGeneralContainer
                key={`${video.analytics_prefix}-${video.video_url}`}
                style={{ padding: "30px 15px 30px 16px" }}
                id={index === 0 ? "carrierInfoVideo" : undefined}
              >
                <ReportVideoEmbed
                  reportType="carrier"
                  url={video.video_url}
                  analyticsPrefix={video.analytics_prefix}
                  requisitionID={requisitionID}
                />
              </CarrierGeneralContainer>
            ))}
          <CarrierGeneralContainer>
            <DetailedResultsExpert />
          </CarrierGeneralContainer>
          <CarrierGeneralContainer>
            <DetailedResultsFAQ />
          </CarrierGeneralContainer>
        </CarrierTabletMobileWrapper>
      </CarrierWrapper>
    </>
  );
};

export default Landing;
